import type { DuAnCongViecTree, DuAnCongViecChiTietView } from '@/types/action-plan/DuAnView';
import type { DashboardRequest } from '@/types/action-plan/DashboardRequest';
import { ACTION_PLAN_V2 } from '@/components/common/ActionPlanV2/constants';
import { actionPlanV2Api } from '@/services/action-plan-v2';
import { resolveTreeSelectPayloadFromIdBoPhan } from '@/components/common/ActionPlanDepartment/navigateCongViecDuAnFromDepartment';
import { isSameEmployeeId } from '@/components/common/ActionPlanV2/core/taskStatus';
import type { FinderUploadedFile, FinderUploadProgress, FinderUploadRequest } from '@/utils/finderJS';

// #region persistIdentity

/** NguoiPhuTrachID đôi khi là "id1,id2" — lấy người đầu. */
export function idPhuTrachDauTien(raw?: string | null): string | undefined {
  const first = String(raw ?? '')
    .split(',')
    .map((part) => part.trim())
    .find(Boolean);
  return first || undefined;
}

export type LoaiTaoCongViec = 'root' | 'child' | 'form';

/**
 * Chọn người phụ trách khi tạo mới.
 * - form: QuickCreate — đúng giá trị form
 * - child: con trên bảng — kế thừa cha, không lấy default phòng
 * - root: công việc gốc — default quản lý phòng, rồi form, rồi user đăng nhập
 */
export function nguoiPhuTrachKhiTaoMoi(args: {
  kind: LoaiTaoCongViec;
  parentNpt?: string | null;
  defaultPhuTrachId?: string | null;
  formNpt?: string | null;
  profileId?: string | null;
}): string | undefined {
  if (args.kind === 'form') {
    return idPhuTrachDauTien(args.formNpt);
  }
  if (args.kind === 'child') {
    return (
      idPhuTrachDauTien(args.parentNpt) ??
      idPhuTrachDauTien(args.formNpt) ??
      idPhuTrachDauTien(args.profileId)
    );
  }
  return (
    idPhuTrachDauTien(args.defaultPhuTrachId) ??
    idPhuTrachDauTien(args.formNpt) ??
    idPhuTrachDauTien(args.profileId)
  );
}

/**
 * Default người phụ trách việc gốc mới (V2): cây chọn đang là NV (`*_nv`) → NV đó; còn lại → người tạo.
 * Không phụ thuộc danh sách NV đã tải (trước đây lấy quản lý phòng theo hash → đổi theo thời điểm tải).
 */
export function macDinhPhuTrachGocV2(
  idSelect?: string | null,
  profileId?: string | null
): string | undefined {
  const selected = String(idSelect ?? '').trim();
  if (selected.toLowerCase().endsWith('_nv')) return selected;
  return idPhuTrachDauTien(profileId);
}

// #endregion persistIdentity

// #region taskAncestry

export const TREE_PATH_SEPARATOR = /\s*>\s*/;

/** Breadcrumb tổ tiên công việc từ treePathName / tenCongViecParent. */
export const buildTaskAncestryBreadcrumb = (
  treeNode: DuAnCongViecTree,
  treePathName = ''
): string => {
  const cv = treeNode.congViec;
  const taskName = String(cv?.tenCongViec ?? '').trim();
  const parentLabel = String(cv?.tenCongViecParent ?? '').trim();
  const path = String(cv?.treePathName ?? treePathName ?? '').trim();

  if (path) {
    const segments = path.split(TREE_PATH_SEPARATOR).map((part) => part.trim()).filter(Boolean);
    if (segments.length > 1) {
      const ancestors =
        segments[segments.length - 1] === taskName
          ? segments.slice(0, -1)
          : segments;
      if (ancestors.length > 0) {
        return ancestors.join(' › ');
      }
    }
    if (segments.length === 1 && segments[0] !== taskName) {
      return segments[0];
    }
  }

  return parentLabel;
};

// #endregion taskAncestry

// #region taskDetailLoader

export type LoadTaskDetailParams = {
  idSelect: string;
  year: number;
  dateChoice?: string;
  tabActive?: string;
  record: DuAnCongViecTree;
};

export async function loadTaskDetail(
  params: LoadTaskDetailParams
): Promise<DuAnCongViecChiTietView | null> {
  const { idSelect, year, dateChoice, tabActive, record } = params;
  const cv = record.congViec;
  if (!cv?.id) return null;

  const idDuAn = cv.duAnID || (cv.isDuAn ? cv.id : '') || '';
  if (!idDuAn) return null;

  // IDChoice = phạm vi đang xem trên header (idSelect), KHÔNG dùng nguoiPhuTrachID của task.
  // Dùng ID người phụ trách khác → GetQuyen theo scope người đó → báo không có quyền khi xem việc đã giao.
  const idChoice = String(idSelect || '').trim();
  if (!idChoice) return null;

  const request: DashboardRequest = {
    id: idChoice,
    idChoice,
    idDuAn,
    idMucTieu: cv.mtctid || '0',
    idCongViec: cv.id,
    year,
    dateChoice,
    hienThi: ACTION_PLAN_V2.HIEN_THI_VIEC_CUA_TOI,
    tabActive: tabActive || ACTION_PLAN_V2.TAB.PHU_TRACH,
    isDetail: true,
    phanHe: ACTION_PLAN_V2.PHAN_HE,
  };
  /* eslint-disable @typescript-eslint/no-explicit-any */
  const res: any = await actionPlanV2Api.chitiet(request);
  if (res?.data) return res.data as DuAnCongViecChiTietView;
  return null;
}

/**
 * idChoice để mở chi tiết 1 việc (vd. chọn từ tìm kiếm) mà KHÔNG đổi BP/NV trên header:
 * header đã đúng phạm vi phụ trách → giữ; khác → BP/NV của người phụ trách (chỉ tính, không ghi store).
 */
export async function resolveTaskDetailIdSelect(params: {
  currentIdSelect: string;
  assigneeId?: string | null;
  treeParams: { idBoPhan?: string; treeSelectValue?: string };
  tenantId?: string | null;
}): Promise<string> {
  const currentId = String(params.currentIdSelect || '').trim();
  const assigneeId = String(params.assigneeId ?? '').split(',')[0].trim();
  const targetId = String(params.treeParams.idBoPhan || '').trim();
  const headerMatches =
    !targetId ||
    currentId === targetId ||
    (!!assigneeId && (currentId === assigneeId || isSameEmployeeId(currentId, assigneeId)));
  if (headerMatches) return currentId;

  const key = String(params.treeParams.treeSelectValue || targetId).trim();
  const payload = await resolveTreeSelectPayloadFromIdBoPhan(key, params.tenantId);
  return String(payload?.idSelect || targetId || currentId).trim();
}

// #endregion taskDetailLoader

// #region fileDropUploadState

export type UploadStatus = 'pending' | 'uploading' | 'success' | 'error';

export interface ClientUploadItem extends FinderUploadRequest {
  name: string;
  status: UploadStatus;
  progress: number;
  error?: string;
}

export function createUploadItems(files: File[]): ClientUploadItem[] {
  return files.map((file, index) => ({
    clientId: `attachment-${Date.now()}-${index}-${Math.random().toString(36).slice(2)}`,
    file,
    name: file.name,
    status: 'pending',
    progress: 0,
  }));
}

export function applyUploadProgress(
  items: ClientUploadItem[],
  update: FinderUploadProgress
): ClientUploadItem[] {
  return items.map((item) =>
    item.clientId === update.clientId
      ? {
          ...item,
          status: update.status,
          progress: Math.max(item.progress, update.progress),
          error: update.error,
        }
      : item
  );
}

export function applyUploadResults(
  items: ClientUploadItem[],
  results: FinderUploadedFile[]
): ClientUploadItem[] {
  const byId = new Map(results.map((result) => [result.clientId, result]));
  return items.map((item) => {
    const result = byId.get(item.clientId);
    if (!result) return item;
    return {
      ...item,
      status: result.status,
      progress: Math.max(item.progress, result.progress),
      error: result.error,
    };
  });
}

export function failUnresolvedUploads(
  items: ClientUploadItem[],
  error: string
): ClientUploadItem[] {
  return items.map((item) =>
    item.status === 'pending' || item.status === 'uploading'
      ? { ...item, status: 'error', error }
      : item
  );
}

export function removeUploadItem(items: ClientUploadItem[], clientId: string): ClientUploadItem[] {
  return items.filter((item) => item.clientId !== clientId);
}

export function hasPendingUploads(items: ClientUploadItem[]): boolean {
  return items.some((item) => item.status === 'pending' || item.status === 'uploading');
}

// #endregion fileDropUploadState
